import React, { ReactNode, Suspense, useState } from "react"
import { classNames } from "@today/lib"
import useSideNavItem from "../../hooks/useSideNavItem"
import SideNavLink from "./SideNavLink"
import { useAuthentication, useUserInfo } from "@today/auth"
import { useRouter } from "next/router"
import Profile from "../../public/images/sidebar/profile.svg"
import Logout from "../../public/images/sidebar/logout.svg"
import SidebarLogo from "./SidebarLogo"
import { Spinner } from "baseui/spinner"
import LoadingFallback from "@today/ui/components/LoadingFallback"
import Link from "next/link"
import { ORDERS_HIDDEN_CLIENT_IDS } from "../../utils/clientIdGroups"

function SideBar() {
  const [isActiveSidebar, setIsActiveSidebar] = useState(true)
  const router = useRouter()
  const { client, routerStartsWithAccount, sideItemsByClient } =
    useSideNavItem()
  const { isAuthenticated, logout } = useAuthentication()
  const { clientId } = useUserInfo()
  const isMeHidden = !!clientId && ORDERS_HIDDEN_CLIENT_IDS.includes(clientId)

  const toggleSidebar = () => setIsActiveSidebar(!isActiveSidebar)

  const isLoadings = routerStartsWithAccount
    ? false
    : isAuthenticated
    ? !client
    : false

  return (
    <div className={`flex h-screen flex-col border-r`}>
      <div
        className={classNames(
          `flex bg-white`,
          isActiveSidebar ? "w-52" : "w-19"
        )}
      >
        <SidebarLogo
          isActiveSidebar={isActiveSidebar}
          toggleSidebar={toggleSidebar}
        />
      </div>
      <div
        className={classNames(
          `flex w-full flex-1 flex-col justify-between pb-10 ${
            isActiveSidebar ? "max-w-52" : "max-w-19"
          }`
        )}
      >
        <LoadingFallback
          hasWaitFor={isLoadings}
          fallback={
            <div className="flex h-40 items-center justify-center">
              <Spinner className="h-6 w-6 " />
            </div>
          }
        >
          <div>
            {/* 사용자 정보가 있을 때만 표시 */}
            {client && !isMeHidden && (
              <Link
                href="/me"
                className="block transition-colors hover:bg-gray-100"
              >
                <div
                  className={classNames(
                    "flex flex-row items-center gap-x-4 px-5 py-4",
                    !isActiveSidebar ? "justify-center" : ""
                  )}
                >
                  <Profile className="h-6 w-6 shrink-0" />
                  {isActiveSidebar && (
                    <>
                      <div className="min-w-0 flex-1 font-normal">
                        <div className="overflow-hidden text-ellipsis whitespace-pre">
                          {`${client?.name}님`}
                        </div>
                      </div>
                      <ExternalLinkIcon className="mb-0.5 h-4 w-4 shrink-0 text-gray-600" />
                    </>
                  )}
                </div>
              </Link>
            )}
            {sideItemsByClient().map(
              ({ title, itemId = "", icon, subItems }, i) => {
                const hasSubItems = subItems && subItems.length > 0

                // 축소 모드이거나 서브아이템이 없으면 기존 렌더링
                if (!hasSubItems || !isActiveSidebar) {
                  return (
                    <SideNavLink key={i} href={itemId}>
                      <Title Icon={icon} isOnlyIcon={!isActiveSidebar}>
                        {title}
                      </Title>
                    </SideNavLink>
                  )
                }

                // 확장 모드 + 서브아이템 있는 경우
                return (
                  <div key={i}>
                    <Title Icon={icon} isOnlyIcon={false}>
                      {title}
                    </Title>
                    {subItems.map((sub, j) => (
                      <SideNavLink key={j} href={sub.itemId}>
                        <div
                          className={classNames(
                            "py-2.5 pl-14 pr-5 text-sm",
                            router.pathname === sub.itemId
                              ? "font-semibold text-gray-900"
                              : "text-gray-500 hover:text-gray-700"
                          )}
                        >
                          {sub.title}
                        </div>
                      </SideNavLink>
                    ))}
                  </div>
                )
              }
            )}
          </div>

          <div>
            {/* 로그인 상태일 때만 로그아웃 버튼 표시 */}
            {isAuthenticated && (
              <button
                className="w-full transition-colors hover:bg-gray-100"
                onClick={() => logout()}
              >
                <Title Icon={Logout} isOnlyIcon={!isActiveSidebar}>
                  {"로그아웃"}
                </Title>
              </button>
            )}
          </div>
        </LoadingFallback>
      </div>
    </div>
  )
}

export default SideBar

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
}

type TitleProps = {
  children: ReactNode
  Icon: React.FC<{ className?: string }>
  isOnlyIcon?: boolean
}
function Title({ children, Icon, isOnlyIcon = false }: TitleProps) {
  return (
    <div
      className={classNames(
        `flex flex-row items-center 
        ${isOnlyIcon ? "justify-center" : ""}
           gap-x-4 px-5 py-4`
      )}
    >
      <Icon className="h-6 w-6" />
      {!isOnlyIcon && (
        <div className=" max-w-32 font-normal">
          <div className="overflow-hidden text-ellipsis whitespace-pre">
            {children}
          </div>
        </div>
      )}
    </div>
  )
}
